/**
 * PatientNoteFormModal — corp's Create / Edit Patient Note dialog, for one
 * account: corp's 960px dialog with its two tabs,
 *   - Details: Name and Subject, both required (corp's rules);
 *   - Content: the note body in corp's editor (RichTextEditor: Visual or
 *     Source, with Insert Shortcode), not required.
 * There is no Category field: a note is added from its category's row and
 * moved by dragging it on the board (as the other Admin boards). An edit loads
 * the body first (the list leaves it out) and sends only what changed. Edits
 * are tracked (pills, Revert, the discard prompt).
 * On a Locked page AdminFormModal makes it a viewer.
 */
import { useEffect, useState } from 'react';
import { Input } from '@/components/ui/Input';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/services/apiClient';
import {
  createPatientNoteTemplate,
  getPatientNoteTemplateContent,
  updatePatientNoteTemplate,
  type PatientNoteTemplate,
  type PatientNoteTemplateForm,
} from '@/services/patientNotesAdmin.service';
import { toast } from '@/store/toastStore';
import { AdminFormModal } from './AdminFormModal';
import { useAdminPageLevel } from './AdminPageLevelContext';
import { DialogField, fieldClasses, type FieldState } from './DialogChrome';
import { RichTextEditor } from './RichTextEditor';
import { useTrackedForm } from './useTrackedForm';

type Tab = 'details' | 'content';

function formFrom(n: PatientNoteTemplate | null, content = ''): PatientNoteTemplateForm {
  return { name: n?.name ?? '', subject: n?.subject ?? '', content };
}

export function patientNoteErrors(f: PatientNoteTemplateForm): string[] {
  const errors: string[] = [];
  if (!f.name.trim()) errors.push('Name is required');
  if (!f.subject.trim()) errors.push('Subject is required');
  return errors;
}

export interface PatientNoteFormModalProps {
  /** Null to add one to `category`. */
  note: PatientNoteTemplate | null;
  /** Where a new note goes (and the name shown for an existing one's category). */
  category: { id: number; name: string } | null;
  onClose: () => void;
  onSaved: (saved: PatientNoteTemplate | null, isNew: boolean) => void;
}

export function PatientNoteFormModal({ note, category, onClose, onSaved }: PatientNoteFormModalProps) {
  const isNew = note === null;
  const locked = useAdminPageLevel() === 'locked';
  const t = useTrackedForm<PatientNoteTemplateForm>(() => formFrom(note), { tracking: !isNew });
  const { form } = t;
  const [tab, setTab] = useState<Tab>('details');
  const [saving, setSaving] = useState(false);
  // An existing note's body is fetched before it can be shown or saved.
  const [contentState, setContentState] = useState<'loading' | 'ready' | 'failed'>(isNew ? 'ready' : 'loading');

  const noteId = note?.id ?? null;
  const resetForm = t.reset;
  useEffect(() => {
    if (noteId === null || !note) return;
    let cancelled = false;
    getPatientNoteTemplateContent(noteId)
      .then((content) => {
        if (cancelled) return;
        resetForm(formFrom(note, content));
        setContentState('ready');
      })
      .catch(() => {
        if (!cancelled) setContentState('failed');
      });
    return () => {
      cancelled = true;
    };
    // Loaded once per note; `note` is the row the modal opened with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteId, resetForm]);

  const errors = [
    ...patientNoteErrors(form),
    ...(contentState === 'failed' ? ['The note content could not be loaded. Close and try again.'] : []),
  ];
  const state = (missing: boolean): FieldState => (missing ? 'error' : 'none');
  const missingName = !form.name.trim();
  const missingSubject = !form.subject.trim();

  async function handleSave() {
    if (errors.length > 0 || saving || contentState !== 'ready') return;
    setSaving(true);
    try {
      if (note) {
        const changes: Partial<PatientNoteTemplateForm> = {};
        for (const key of t.changed) changes[key] = form[key];
        await updatePatientNoteTemplate(note.id, changes);
        toast.success('Patient note updated');
        onSaved(
          { ...note, name: form.name.trim(), subject: form.subject.trim(), hasContent: form.content.trim() !== '' },
          false,
        );
      } else {
        if (!category) return;
        const saved = await createPatientNoteTemplate(form, category.id);
        toast.success('Patient note created');
        onSaved(saved.id ? saved : null, true);
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to save patient note'));
    } finally {
      setSaving(false);
    }
  }

  const where = category ? `In ${category.name}. ` : '';
  const description = locked ? where.trim() || undefined : `${where}Name and subject are required.`;

  const tabButton = (id: Tab, label: string) => (
    <button
      type="button"
      role="tab"
      aria-selected={tab === id}
      onClick={() => setTab(id)}
      className={cn(
        'rounded-md px-4 py-1.5 text-sm font-medium transition-colors',
        tab === id ? 'bg-blue-600 text-white' : 'text-slate-500 hover:bg-slate-100',
      )}
    >
      {label}
    </button>
  );

  return (
    <AdminFormModal
      title={locked ? 'Patient Note' : `${isNew ? 'Create' : 'Edit'} Patient Note`}
      description={description}
      isNew={isNew}
      dirty={t.dirty}
      saving={saving}
      errors={errors}
      canSave={contentState === 'ready'}
      onSave={() => void handleSave()}
      onRevertAll={t.revertAll}
      onClose={onClose}
      size="xl"
      panelClassName="md:max-w-[960px]"
      pinned={
        <div role="tablist" aria-label="Patient note" className="flex items-center gap-1">
          {tabButton('details', 'Details')}
          {tabButton('content', 'Content')}
          <span className="ml-1">{t.badge('content', 'Content')}</span>
        </div>
      }
    >
      {contentState === 'loading' ? (
        <SpinnerOverlay label="Loading note…" />
      ) : tab === 'details' ? (
        <div className="grid gap-4">
          <DialogField
            label="Name"
            htmlFor="patient-note-name"
            required
            state={state(missingName)}
            labelHint={missingName ? '— required' : undefined}
            badge={t.badge('name', 'Name')}
          >
            <Input
              id="patient-note-name"
              value={form.name}
              // note_templates.name is varchar(100).
              maxLength={100}
              placeholder="Note name"
              onChange={(e) => t.patch({ name: e.target.value })}
              className={fieldClasses.input(state(missingName))}
            />
          </DialogField>

          <DialogField
            label="Subject"
            htmlFor="patient-note-subject"
            required
            state={state(missingSubject)}
            labelHint={missingSubject ? '— required' : undefined}
            badge={t.badge('subject', 'Subject')}
          >
            <Input
              id="patient-note-subject"
              value={form.subject}
              maxLength={255}
              placeholder="Note subject"
              onChange={(e) => t.patch({ subject: e.target.value })}
              className={fieldClasses.input(state(missingSubject))}
            />
          </DialogField>
        </div>
      ) : (
        <div className={cn(contentState === 'failed' && 'hidden')}>
          <RichTextEditor
            id="patient-note-content"
            shortcodes
            value={form.content}
            onChange={(html) => t.patch({ content: html })}
            placeholder="Note content…"
          />
        </div>
      )}
    </AdminFormModal>
  );
}
